import { Link, useLocation } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store';
import { usePlatformNavStore } from '@/store/usePlatformNavStore';
import {
  getFirstAccessiblePlatformPath,
  getFirstAccessiblePlatformPathIgnoringMenu,
  getFirstAccessibleTenantPath,
  getFirstAccessibleTenantPathIgnoringMenu,
} from '@/lib/route-access';
import type { ForbiddenNavigateState } from '@/lib/forbidden-route-state';

export type { ForbiddenReason, ForbiddenNavigateState } from '@/lib/forbidden-route-state';

/**
 * 403：路由守卫拒绝（权限或侧栏 menuKeys 不包含目标 path）。
 * 不作为 Require* 子路由，避免循环重定向。
 */
export function ForbiddenPage() {
  const location = useLocation();
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const menuKeys = useAuthStore((s) => s.user?.menuKeys);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const dynamic = usePlatformNavStore((s) => s.routePermissionOverrides);
  const st = location.state as ForbiddenNavigateState | null;
  const from = st?.from;
  const reason = st?.reason;

  const home = isAdmin
    ? getFirstAccessiblePlatformPath(perms, menuKeys, dynamic) ??
      getFirstAccessiblePlatformPathIgnoringMenu(perms, dynamic) ??
      '/dashboard'
    : getFirstAccessibleTenantPath(perms, menuKeys) ??
      getFirstAccessibleTenantPathIgnoringMenu(perms) ??
      '/workspace';

  const pathLine =
    from != null && from.length > 0 ? (
      <>
        无法打开路径{' '}
        <code className="rounded bg-zinc-900 px-1.5 py-0.5 font-mono text-xs text-cyan-200/90">
          {from}
        </code>
        。
      </>
    ) : null;

  const detail =
    reason === 'permission' ? (
      <span>
        <strong className="text-zinc-200">权限码不足</strong>
        ：当前角色未持有该入口所需的平台/租户权限（含通配规则）。请联系管理员在「平台角色 /
        租户角色」中调整权限绑定。
      </span>
    ) : reason === 'menu' ? (
      <span>
        <strong className="text-zinc-200">侧栏菜单白名单未包含该路径</strong>
        ：你已有对应权限，但角色配置的侧栏菜单未勾选该入口。可在「平台角色 / 租户角色」编辑里补充侧栏菜单，或由管理员更新你的角色。
      </span>
    ) : reason === 'none' ? (
      <span>
        <strong className="text-zinc-200">无可进入的首页</strong>
        ：在<strong className="text-zinc-300">权限</strong>与
        <strong className="text-zinc-300">侧栏菜单</strong>
        组合下没有任何可访问路由（例如菜单白名单与权限求交后为空）。请联系管理员调整角色或菜单配置。
      </span>
    ) : from ? (
      <span>
        请确认<strong className="text-zinc-300">权限码</strong>与
        <strong className="text-zinc-300">侧栏菜单白名单</strong>
        均包含该入口（二者求交，与左侧菜单可见性一致）。
      </span>
    ) : (
      <span>
        当前账号在<strong className="text-zinc-300">权限</strong>与
        <strong className="text-zinc-300">侧栏菜单</strong>
        组合下无可匹配页面，或目标路径未在菜单白名单中。
      </span>
    );

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4 text-center">
      <ShieldX className="h-16 w-16 text-amber-500/80" aria-hidden />
      <div className="max-w-lg">
        <h1 className="font-['Syne',sans-serif] text-2xl font-bold tracking-tight text-zinc-50">
          403 · 无权访问
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
          {pathLine}
          {detail}
        </p>
      </div>
      <Button
        asChild
        variant="outline"
        className="border-cyan-500/35 bg-zinc-950/60 text-cyan-100 hover:border-cyan-400/45 hover:bg-cyan-500/10"
      >
        <Link to={home} replace>
          返回可访问首页
        </Link>
      </Button>
    </div>
  );
}
